/**
 * Copyright 2025 Lumavec Group LLC
 *
 * All rights reserved. No part of this software may be used, copied, modified,
 * or distributed without explicit written permission. This software is provided
 * "as-is" without warranty of any kind, express or implied, including but not
 * limited to warranties of merchantability or fitness for a particular purpose.
 */

/**
 * Chat Message Management
 * 
 * Handles message CRUD operations, database persistence, and Honcho integration.
 */

import { QueryClient } from '@tanstack/react-query';
import { createMutationFn } from "@shared/lib/api";
import {
  getChatMessages,
  createChatMessage,
  updateChatMessage
} from '@shared/lib/api/chat';
import {
  MessageResponseData
} from '@shared/types/responses/honcho';
import { ToolCall, EntityLink } from '@shared/types/responses/streaming';
import { updateChatSession } from '@shared/lib/api/chat';
import { ChatState, ChatAction, ChatMessage } from './types';

export class MessageManager {
  constructor(
    private dispatch: React.Dispatch<ChatAction>,
    private queryClient: QueryClient,
    private user: any
  ) {}

  /**
   * Load messages from database for a session
   */
  async loadMessages(sessionId?: number): Promise<void> {
    if (!sessionId) return;
    try {
      const messagesResult = await getChatMessages(sessionId);
      
      const messages: ChatMessage[] = messagesResult.map(msg => ({
        id: msg.uuid || msg.id?.toString(), // Use UUID as primary ID, fallback to DB ID
        uuid: msg.uuid, // Include UUID field
        role: msg.role as 'user' | 'assistant',
        content: msg.content,
        timestamp: msg.timestamp ? new Date(msg.timestamp) : new Date(),
        actionPlan: msg.actionPlan as any,
        toolCalls: msg.toolCalls as ToolCall[] | undefined,
        entityLinks: msg.entityLinks as EntityLink[] | undefined,
        executionState: msg.executionState as ChatMessage['executionState'],
        metadata: msg.metadata as ChatMessage['metadata']
      }));

      this.dispatch({ type: 'SET_MESSAGES', payload: messages });
    } catch (error) {
      console.error('Error loading messages:', error);
    }
  }

  /**
   * Add message and persist to database and Honcho
   * 
   * Messages are saved to database first to get UUIDs, then added to React state
   * with consistent IDs that SSE events can target.
   */
  async addMessage(message: ChatMessage, state: ChatState, sessionId?: number): Promise<any> {
    // Initialize Honcho if not already done
    if (!state.isInitialized) {
      await this.initializeHoncho(state);
    }

    // Persist to our database first to get UUID
    const targetSessionId = sessionId || state.dbSessionId;
    if (targetSessionId) {
      try {
        const savedMessage = await createChatMessage({
          sessionId: targetSessionId,
          role: message.role,
          content: message.content,
          senderUserId: message.role === 'user' ? this.user?.id : undefined,
          senderAgentId: message.role === 'assistant' ? message.metadata?.agentId : undefined,
          senderPeerId: message.metadata?.peerId,
          actionPlan: message.actionPlan,
          toolCalls: message.toolCalls,
          entityLinks: message.entityLinks,
          executionState: message.executionState,
          metadata: message.metadata,
          processingTime: message.metadata?.processingTime,
          tokenCount: message.metadata?.tokenCount
        });
        
        if (!savedMessage) {
          console.error('❌ createChatMessage returned null/undefined');
          throw new Error('Failed to create message - no response from API');
        }
        
        // Create the final message with UUID for state
        const finalMessage: ChatMessage = {
          ...message,
          id: savedMessage.uuid || savedMessage.id?.toString(),
          uuid: savedMessage.uuid
        };
        
        // Now add to local state with correct UUID
        this.dispatch({ type: 'ADD_MESSAGE', payload: finalMessage });
        
        // Invalidate React Query cache to trigger real-time UI updates
        this.queryClient.invalidateQueries({ queryKey: ['/api/chat/sessions'] });
        
        // Return the database ID for use by caller
        return savedMessage;
        
      } catch (error) {
        console.error('❌ Error persisting message to database:', error);
        console.error('❌ Full error details:', error);
        throw error; // Re-throw to prevent execution from continuing
      }
    }

    // Also persist to Honcho for AI memory
    if (state.workspaceId && state.honchoSessionId) {
      try {
        await createMutationFn<MessageResponseData, any>('/api/agent/honcho/messages', 'POST')({
          workspaceId: state.workspaceId,
          sessionId: state.honchoSessionId,
          content: message.content,
          role: message.role,
          metadata: {
            timestamp: message.timestamp.toISOString(),
            ...(message.actionPlan && { actionPlan: message.actionPlan }),
            ...message.metadata
          }
        });
      } catch (error) {
        console.error('Error persisting message to Honcho:', error);
      }
    }
  }

  /**
   * Initialize Honcho workspace, peer, and session.
   * If Honcho is disabled or unavailable, this marks the chat as initialized
   * and continues without Honcho memory support.
   */
  async initializeHoncho(state: ChatState): Promise<void> {
    if (state.isInitialized) return;

    try {
      this.dispatch({ type: 'SET_LOADING', payload: true });

      // First check if Honcho is enabled and available
      try {
        const statusResponse = await fetch('/api/agent/honcho/status');
        const statusData = await statusResponse.json();
        const status = statusData.data || statusData;
        
        if (!status.enabled || !status.available) {
          // Honcho is disabled or unavailable - continue without it
          console.info('Honcho unavailable:', status.message);
          this.dispatch({ type: 'SET_INITIALIZED', payload: true });
          
          // Load existing messages if this is a restored session
          if (state.dbSessionId) {
            await this.loadMessages(state.dbSessionId);
          }
          return;
        }
      } catch (statusError) {
        // If we can't even check status, assume Honcho is unavailable
        console.info('Could not check Honcho status, continuing without Honcho memory:', statusError);
        this.dispatch({ type: 'SET_INITIALIZED', payload: true });
        
        if (state.dbSessionId) {
          await this.loadMessages(state.dbSessionId);
        }
        return;
      }

      // Create or get workspace
      const workspaceResult = await createMutationFn<any, any>('/api/agent/honcho/workspaces', 'POST')({
        name: 'skillshop',
        metadata: { description: 'Skillshop chat workspace' }
      });
      
      const workspaceId = workspaceResult.id;

      // Create or get peer for the current user
      const peerResult = await createMutationFn<any, any>('/api/agent/honcho/peers', 'POST')({
        workspaceId,
        name: this.user?.id ? `user_${this.user.id}` : 'user',
        metadata: { type: 'user', userId: this.user?.id }
      });
      
      const peerId = peerResult.id;

      // Create a new session
      const sessionResult = await createMutationFn<any, any>('/api/agent/honcho/sessions', 'POST')({
        workspaceId,
        metadata: { type: 'skillshop-chat' }
      });
      
      const sessionId = sessionResult.id;

      this.dispatch({ 
        type: 'SET_HONCHO_STATE', 
        payload: { workspaceId, honchoSessionId: sessionId, peerId }
      });
      this.dispatch({ type: 'SET_INITIALIZED', payload: true });

      // Update our database session with Honcho IDs
      if (state.dbSessionId) {
        try {
          await updateChatSession(state.dbSessionId, {
            honchoWorkspaceId: workspaceId,
            honchoSessionId: sessionId,
            honchoUserPeerId: peerId,
            isInitialized: true
          });
        } catch (error) {
          console.error('Error updating session with Honcho state:', error);
        }
      }

      // Load existing messages if this is a restored session
      if (state.dbSessionId) {
        await this.loadMessages(state.dbSessionId);
      }
    } catch (error) {
      console.error('Error initializing Honcho:', error);
      // Even if Honcho fails, mark as initialized to prevent infinite retry loop
      this.dispatch({ type: 'SET_INITIALIZED', payload: true });
      
      // Still load messages from our database
      if (state.dbSessionId) {
        await this.loadMessages(state.dbSessionId);
      }
    } finally {
      this.dispatch({ type: 'SET_LOADING', payload: false });
    }
  }

  /**
   * Update session title
   */
  updateSessionTitle(title: string): void {
    this.dispatch({ type: 'SET_SESSION_TITLE', payload: title });
  }

  /**
   * Update message execution state
   */
  updateMessageExecutionState(messageId: string, executionState: Partial<ChatMessage['executionState']>): void {
    this.dispatch({ type: 'UPDATE_MESSAGE_EXECUTION_STATE', payload: { messageId, executionState } });
  }
}
